package cz.lhoracek.mapl.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.viewinterop.WebElementView
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.Promise
import mapl.composeapp.generated.resources.Res
import mapl.composeapp.generated.resources.qr_scanner_web_error
import mapl.composeapp.generated.resources.qr_scanner_web_permission_denied
import mapl.composeapp.generated.resources.qr_scanner_web_scanning
import mapl.composeapp.generated.resources.qr_scanner_web_starting
import mapl.composeapp.generated.resources.qr_scanner_web_unsupported
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalComposeUiApi::class)
@Composable
actual fun QrScannerView(onResult: (String) -> Unit, modifier: Modifier) {
    val onResultState by rememberUpdatedState(onResult)
    val undefined = js("undefined")
    val isSecureContext = window.asDynamic().isSecureContext as? Boolean ?: false
    val mediaDevices = window.navigator.asDynamic().mediaDevices
    val hasGetUserMedia = mediaDevices != null &&
        mediaDevices != undefined &&
        mediaDevices.getUserMedia != null &&
        mediaDevices.getUserMedia != undefined
    val hasBarcodeDetector = js("globalThis.BarcodeDetector") != undefined
    val isPreviewSupported = isSecureContext && hasGetUserMedia && hasBarcodeDetector

    var videoElement by remember { mutableStateOf<Any?>(null) }
    var scannerState by remember { mutableStateOf(QrScannerWebState.Starting) }

    LaunchedEffect(isPreviewSupported) {
        if (!isPreviewSupported) {
            scannerState = QrScannerWebState.Unsupported
        }
    }

    DisposableEffect(videoElement, isPreviewSupported) {
        if (!isPreviewSupported || videoElement == null) {
            return@DisposableEffect onDispose { }
        }

        scannerState = QrScannerWebState.Starting

        var stream: dynamic = null
        var intervalId: Int? = null
        var stopped = false

        fun cleanupCamera() {
            intervalId?.let { window.clearInterval(it) }
            intervalId = null

            val tracks: dynamic = stream?.getTracks?.call(stream)
            val tracksCount = (tracks?.length as? Int) ?: 0
            for (i in 0 until tracksCount) {
                tracks[i]?.stop?.call(tracks[i])
            }

            val currentVideo = videoElement?.asDynamic()
            currentVideo?.pause?.call(currentVideo)
            currentVideo?.srcObject = null
            stream = null
        }

        fun detectLoop(detector: dynamic) {
            intervalId = window.setInterval(
                {
                    if (stopped) return@setInterval
                    val localVideo = videoElement?.asDynamic() ?: return@setInterval
                    val readyState = (localVideo.readyState as? Int) ?: 0
                    if (readyState < 2) return@setInterval

                    detector.detect(localVideo).then { codes: dynamic ->
                        if (stopped) return@then
                        val count = (codes?.length as? Int) ?: 0
                        if (count <= 0) return@then

                        val rawValue = codes[0]?.rawValue as? String ?: return@then
                        if (rawValue.isBlank()) return@then

                        stopped = true
                        cleanupCamera()
                        onResultState(rawValue)
                    }.catch { _ -> Unit }
                },
                250,
            )
        }

        val constraints = js("{ video: { facingMode: { ideal: 'environment' } }, audio: false }")
        (mediaDevices.getUserMedia(constraints) as Promise<dynamic>).then { localStream: dynamic ->
            if (stopped) return@then
            stream = localStream

            val localVideo = videoElement?.asDynamic() ?: return@then
            localVideo.srcObject = localStream
            (localVideo.play() as? Promise<dynamic>)?.catch { _ -> Unit }

            val detector = js("new BarcodeDetector({ formats: ['qr_code'] })")
            scannerState = QrScannerWebState.Scanning
            detectLoop(detector)
        }.catch { error: dynamic ->
            if (stopped) return@catch
            scannerState = when (error?.name as? String) {
                "NotAllowedError", "SecurityError" -> QrScannerWebState.PermissionDenied
                else -> QrScannerWebState.Error
            }
            cleanupCamera()
        }

        onDispose {
            stopped = true
            cleanupCamera()
        }
    }

    val message = when (scannerState) {
        QrScannerWebState.Starting -> stringResource(Res.string.qr_scanner_web_starting)
        QrScannerWebState.Scanning -> stringResource(Res.string.qr_scanner_web_scanning)
        QrScannerWebState.PermissionDenied -> stringResource(Res.string.qr_scanner_web_permission_denied)
        QrScannerWebState.Unsupported -> stringResource(Res.string.qr_scanner_web_unsupported)
        QrScannerWebState.Error -> stringResource(Res.string.qr_scanner_web_error)
    }

    Box(modifier = modifier) {
        if (isPreviewSupported) {
            WebElementView(
                factory = {
                    val element = document.createElement("video").asDynamic()
                    element.setAttribute("playsinline", "true")
                    element.muted = true
                    element.autoplay = true
                    element.style.width = "100%"
                    element.style.height = "100%"
                    element.style.objectFit = "cover"
                    element.style.backgroundColor = "#000"
                    element
                },
                modifier = Modifier.fillMaxSize(),
                update = { element ->
                    videoElement = element
                },
                onRelease = { element ->
                    if (videoElement == element) {
                        videoElement = null
                    }
                },
            )
        }

        if (scannerState != QrScannerWebState.Scanning) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center,
            ) {
                Text(message)
            }
        }
    }
}

private enum class QrScannerWebState {
    Starting,
    Scanning,
    PermissionDenied,
    Unsupported,
    Error,
}
